// src/services/discoveryService.js
import { collection, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../config/firebase';

class DiscoveryService {
  // Get discoverable showcases
  async getDiscoverableShowcases(filters = {}, lastDoc = null, pageSize = 24) {
    try {
      let q = query(
        collection(db, 'pb-books'),
        where('discoveryEnabled', '==', true),
        where('visibility', '==', 'public'),
        where('albumCategory', '==', 'showcase')
      );

      // Apply filters
      if (filters.showcaseSubcategory && filters.showcaseSubcategory !== 'all') {
        q = query(q, where('showcaseSubcategory', '==', filters.showcaseSubcategory));
      }

      if (filters.tags && filters.tags.length > 0) {
        q = query(q, where('discoveryTags', 'array-contains-any', filters.tags.slice(0, 10)));
      }

      // Sorting
      const sortBy = filters.sortBy || 'trending';
      if (sortBy === 'recent') {
        q = query(q, orderBy('createdAt', 'desc'));
      } else if (sortBy === 'popular') {
        q = query(q, orderBy('analytics.views', 'desc'));
      } else {
        // Trending - combination of recent + engagement
        q = query(q, orderBy('analytics.engagementScore', 'desc'));
      }

      // Pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      q = query(q, limit(pageSize));

      const snapshot = await getDocs(q);
      const showcases = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastDoc: doc
      }));

      return {
        showcases,
        hasMore: snapshot.docs.length === pageSize,
        lastDoc: snapshot.docs[snapshot.docs.length - 1]
      };
    } catch (error) {
      console.error('Error fetching discoverable showcases:', error);
      throw error;
    }
  }

  // Search showcases
  async searchShowcases(searchTerm, filters = {}) {
    try {
      // Use Firestore text search or Algolia for better search
      const showcases = await this.getDiscoverableShowcases(filters);
      
      // Client-side filtering for now (consider Algolia for production)
      const filtered = showcases.showcases.filter(showcase => {
        const searchLower = searchTerm.toLowerCase();
        return (
          showcase.botName?.toLowerCase().includes(searchLower) ||
          showcase.description?.toLowerCase().includes(searchLower) ||
          showcase.discoveryTags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
      });

      return { ...showcases, showcases: filtered };
    } catch (error) {
      console.error('Error searching showcases:', error);
      throw error;
    }
  }

  // Get trending tags
  async getTrendingTags(limit = 20) {
    // Implement tag analytics tracking
    // For now, return most used tags
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'pb-books'),
          where('discoveryEnabled', '==', true),
          limit(100)
        )
      );

      const tagFrequency = {};
      snapshot.docs.forEach(doc => {
        const tags = doc.data().discoveryTags || [];
        tags.forEach(tag => {
          tagFrequency[tag] = (tagFrequency[tag] || 0) + 1;
        });
      });

      return Object.entries(tagFrequency)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([tag, count]) => ({ tag, count }));
    } catch (error) {
      console.error('Error fetching trending tags:', error);
      return [];
    }
  }

  // Track engagement for algorithm
  async trackEngagement(showcaseId, engagementType) {
    // Update engagement score for discovery algorithm
    // Types: 'view', 'like', 'share', 'click', 'dwell'
    const weights = {
      view: 1,
      click: 3,
      like: 5,
      share: 10,
      dwell: 2 // Time spent
    };

    // Implement engagement tracking
  }
}

export const discoveryService = new DiscoveryService();